import Styles from './styles.module.css';
import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getFirestore, onSnapshot, query, serverTimestamp, Timestamp, updateDoc, where } from 'firebase/firestore';
import { RxCross2 } from 'react-icons/rx';
import { FaRegCircle } from 'react-icons/fa6';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { IoRefreshOutline } from 'react-icons/io5';
import CompanyDefaultImage from '../../image/default/home.png';
import { generateAbsenceRequestPdf, type AbsenceRequestPdfType } from './pdfApi';

// 公欠申請(学生アプリ「就職活動報告」から自校宛てに出された提出済みのもの)。
// 2026-11-04、保存先を旧absenceRequestsからjobActivityReportsへ切り替え(学生アプリ側は既に
// 移行済み。旧absenceRequestsの既存データはそのまま残すが、この画面では表示しない)。
// hasAbsence(公欠取得の有無)に関わらずすべて出す。公欠取得なし(hasAbsence !== true)の
// 報告は、許可時に公欠取得時間(Ｈ１２３４５６Ｈ)を選ぶ意味がないのでポップアップを出さず
// そのまま許可する(PeriodPickerDialog参照)。
// 「許可待ち」(submitted)・「許可済み」(approved)・「不許可」(rejected)のタブで分ける。
// 許可待ちタブの行の右アイコンは「詳細 / 許可(○) / 許可しない(×)」(押すとすぐ保存、確認は出さない)。
// 許可済み・不許可タブでは、どちらを選んだかの印は出さず「詳細 / 許可待ちに戻す」の2つだけにする
// (押し間違いの取り消し用。一方から他方へ直接は変えられない、必ず許可待ちを経由する)。
// 学校が書き込めるのは status と approvedAt / rejectedAt、updatedAt、approvedPeriods だけ。
// 権限は firestore.rules の match /jobActivityReports。

// 就職活動用紙の「公欠取得時間(担任記入)」欄(Ｈ１２３４５６Ｈ)に対応する選択肢。
// hr1/hr2は1時間目の前・6時間目の後のＨ(ホームルーム)。functions/absenceRequestPdf.jsのPERIOD_ORDERと対応を揃える。
const PERIOD_ORDER = ['hr1', '1', '2', '3', '4', '5', '6', 'hr2'] as const;
const PERIOD_LABELS: Record<string, string> = {
  hr1: 'Ｈ', '1': '1', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6', hr2: 'Ｈ',
};

const TYPE_LABELS: Record<string, string> = {
  salonVisit: 'サロン見学',
  infoSession: '会社説明会',
  interview: '面接・選考',
  webInterview: 'WEB採用試験',
  webInfoSession: 'WEB説明会',
  training: '美容室での研修会',
  other: 'その他',
};

type Status = 'submitted' | 'approved' | 'rejected';

const TABS: [Status, string][] = [
  [ 'submitted', '許可待ち' ],
  [ 'approved', '許可済み' ],
  [ 'rejected', '不許可' ],
];

type AbsenceRequest = {
  id: string;
  uid: string;
  recruitId: string;
  status: Status;
  studentName: string;
  type: string;
  typeDetail: string;
  companyName: string;
  storeName: string;
  date: Date | null;
  startTime: string;
  endTime: string;
  place: string;
  submittedAt: Date | null;
  decidedAt: Date | null; // 許可・不許可にした日時(approvedAt / rejectedAt)
  approvedPeriods: string[]; // 許可するときに選んだ公欠取得時間(PERIOD_ORDERの部分集合)
  hasAbsence: boolean | null; // 公欠取得の有無(学生が報告ごとに選ぶ)。falseなら公欠取得時間を選ぶ必要がない
};

// 「その他」は学生が書いた内容を添えて「その他(内容)」と出す。
const typeLabel = (r: AbsenceRequest) =>
  r.type === 'other' && r.typeDetail ? `その他(${r.typeDetail})` : (TYPE_LABELS[r.type] ?? 'その他');

const logoUrl = (recruitId: string) =>
  `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/companies_logo%2Fresize_images%2F${recruitId.replace(/_\d{4}$/, '')}_200x200?alt=media&token=`;

const toDate = (v: unknown): Date | null => (v instanceof Timestamp ? v.toDate() : null);
const formatShortDate = (d: Date | null) => (d ? `${d.getMonth() + 1}/${d.getDate()}` : '-');
// 検索は大文字・小文字と全角・半角を区別しない(「Bella」でも「bella」「ＢＥＬＬＡ」でも当たる)
const normalize = (v: string) => v.normalize('NFKC').toLowerCase();
const WEEK = ['日', '月', '火', '水', '木', '金', '土'];

type Props = {
  schoolId: string;
  allUsers: any[];
  // 上の絞り込み(コース・学年・クラス。ほかの学生のページと同じ SchoolSearch)。何も選んでいなければ全員
  hashTags: string[];
  filteredUsers: any[];
};

const AbsenceRequests: React.FC<Props> = ({ schoolId, allUsers, hashTags, filteredUsers }) => {
  const [ items, setItems ] = useState<AbsenceRequest[]>([]);
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState(false);
  const [ text, setText ] = useState('');
  const [ tab, setTab ] = useState<Status>('submitted');
  const [ detail, setDetail ] = useState<AbsenceRequest | null>(null);
  // 保存中の申請(○・× の二度押しを防ぐ)
  const [ savingId, setSavingId ] = useState('');
  // ○(許可する)を押した直後に出す、公欠取得時間を選ぶポップアップの対象
  const [ periodPicker, setPeriodPicker ] = useState<AbsenceRequest | null>(null);
  // 許可済みタブの「参加証明書を出力」「就職活動用紙を出力」。どれか1つ出力中は他も含めて押せなくする
  const [ pdfLoadingKey, setPdfLoadingKey ] = useState('');

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    // hasAbsence(公欠取得の有無)に関わらず、就職活動報告はすべて出す(公欠なしの報告も学校が確認できるように)。
    const q = query(
      collection(getFirestore(), 'jobActivityReports'),
      where('schoolId', '==', schoolId),
      where('status', 'in', ['submitted', 'approved', 'rejected']),
    );
    return onSnapshot(q, (snap) => {
      setItems(snap.docs.map((d) => {
        const x = d.data();
        const status: Status = x.status === 'approved' || x.status === 'rejected' ? x.status : 'submitted';
        return {
          id: d.id,
          uid: x.uid ?? '',
          recruitId: x.recruitId ?? '',
          status,
          studentName: x.studentName ?? '',
          type: x.type ?? 'other',
          typeDetail: x.typeDetail ?? '',
          companyName: x.companyName ?? '',
          storeName: x.storeName ?? '',
          date: toDate(x.date),
          startTime: x.startTime ?? '',
          endTime: x.endTime ?? '',
          place: x.place ?? '',
          submittedAt: toDate(x.submittedAt),
          decidedAt: toDate(status === 'approved' ? x.approvedAt : x.rejectedAt),
          approvedPeriods: Array.isArray(x.approvedPeriods) ? x.approvedPeriods : [],
          hasAbsence: typeof x.hasAbsence === 'boolean' ? x.hasAbsence : null,
        };
      }));
      setLoading(false);
      setError(false);
    }, (e) => {
      console.error('公欠申請を読み込めませんでした', e);
      setLoading(false);
      setError(true);
    });
  }, [schoolId]);

  // 許可する(○、公欠取得時間を選んでから) / 許可しない(×)。押すとすぐ保存する
  const decide = async (r: AbsenceRequest, approve: boolean, approvedPeriods?: string[]) => {
    if (savingId || r.status !== 'submitted') return;
    setSavingId(r.id);
    try {
      await updateDoc(doc(getFirestore(), 'jobActivityReports', r.id), approve
        ? { status: 'approved', approvedAt: serverTimestamp(), updatedAt: serverTimestamp(), ...(approvedPeriods?.length ? { approvedPeriods } : {}) }
        : { status: 'rejected', rejectedAt: serverTimestamp(), updatedAt: serverTimestamp() });
    } catch (e) {
      console.error('公欠申請を更新できませんでした', e);
      window.alert('更新できませんでした。時間をおいて、もう一度お試しください。');
    } finally {
      setSavingId('');
    }
  };

  // 許可済み・不許可を「許可待ち」に戻す(押し間違いの取り消し用)。押すとすぐ保存する
  const resetToPending = async (r: AbsenceRequest) => {
    if (savingId || r.status === 'submitted') return;
    setSavingId(r.id);
    try {
      await updateDoc(doc(getFirestore(), 'jobActivityReports', r.id), {
        status: 'submitted',
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error('公欠申請を許可待ちに戻せませんでした', e);
      window.alert('更新できませんでした。時間をおいて、もう一度お試しください。');
    } finally {
      setSavingId('');
    }
  };

  // 参加証明書・就職活動用紙のPDF出力(許可済みタブの行から直接出力する)
  const exportPdf = async (r: AbsenceRequest, docType: AbsenceRequestPdfType) => {
    if (pdfLoadingKey) return;
    setPdfLoadingKey(`${r.id}:${docType}`);
    try {
      const { downloadUrl } = await generateAbsenceRequestPdf(r.id, docType);
      window.open(downloadUrl, '_blank');
    } catch (e) {
      console.error('PDFを生成できませんでした', e);
      window.alert('PDFを生成できませんでした。時間をおいて、もう一度お試しください。');
    } finally {
      setPdfLoadingKey('');
    }
  };

  // 学生のクラス(学校と連携で登録した学科・学年・クラス)を名前の下に出す
  const classOf = useMemo(() => {
    const map = new Map<string, string>();
    allUsers.forEach((u) => {
      const e = u?.education ?? {};
      const label = [e.department, e.grade, e.class].filter((v) => typeof v === 'string' && v.trim()).join(' ');
      const uid = u?.id ?? u?.uid;
      if (uid) map.set(uid, label);
    });
    return map;
  }, [allUsers]);

  // 上の絞り込み(コース・学年・クラス)に当てはまる学生。何も選んでいなければ null(全員)
  const allowedUids = useMemo(() => {
    if (hashTags.length === 0) return null;
    return new Set(filteredUsers.map((u) => u?.id ?? u?.uid).filter(Boolean));
  }, [hashTags, filteredUsers]);

  const inFilter = useMemo(
    () => items.filter((r) => !allowedUids || allowedUids.has(r.uid)),
    [items, allowedUids],
  );

  // 申請日の新しい順。フリーワード(学生名・サロン名・店舗名・場所)で絞り込む
  const rows = useMemo(() => {
    const t = normalize(text.trim());
    return inFilter
      .filter((r) => r.status === tab)
      .filter((r) => !t || normalize(`${r.studentName} ${r.companyName} ${r.storeName} ${r.place}`).includes(t))
      .sort((a, b) => (b.submittedAt?.getTime() ?? 0) - (a.submittedAt?.getTime() ?? 0));
  }, [inFilter, text, tab]);

  const countOf = (status: Status) => inFilter.filter((r) => r.status === status).length;

  return (
    <>
    {/* 見出しは出さず、ほかの画面と同じく表だけにする。表の外の上に、タブと検索欄 */}
    <div className={Styles.toolbar}>
      <div className={Styles.tabs}>
        {TABS.map(([ key, label ]) => (
          <button
            key={key}
            type="button"
            className={`${Styles.tab} ${tab === key ? Styles.tabActive : ''}`}
            onClick={() => setTab(key)}
          >
            {label}<span className={Styles.tabCount}>{countOf(key)}</span>
          </button>
        ))}
      </div>
      <input
        className={Styles.search}
        placeholder="学生名・サロン名・場所で検索"
        value={text}
        onChange={(e) => setText(e.target.value)} />
    </div>

    <div className={Styles.table}>
      <div className={Styles.nav}>
        <p className={Styles.colSubmitted}>申請日</p>
        <p className={Styles.colName}>学生名</p>
        <p className={Styles.colStore}>サロン名</p>
        <p className={Styles.colType}>申請種別</p>
        <p className={Styles.colSchedule}>日程</p>
        <p className={Styles.colPlace}>場所</p>
        <p className={Styles.colIcon}>詳細</p>
        {tab === 'submitted' ? (
          <>
            <p className={Styles.colIcon}>許可</p>
            <p className={Styles.colIcon}>許可しない</p>
          </>
        ) : tab === 'approved' ? (
          <>
            <p className={Styles.colPdf}>PDFのダウンロード</p>
            <p className={Styles.colIconWide}>許可待ちに戻す</p>
          </>
        ) : (
          <p className={Styles.colIconWide}>許可待ちに戻す</p>
        )}
      </div>

      {loading ? (
        <p className={Styles.empty}>読み込み中...</p>
      ) : error ? (
        <p className={Styles.empty}>公欠申請を読み込めませんでした。時間をおいて、もう一度お試しください。</p>
      ) : rows.length === 0 ? (
        <p className={Styles.empty}>
          {text ? '該当する申請はありません。'
            : tab === 'submitted' ? '許可待ちの公欠申請はありません。'
            : tab === 'approved' ? '許可した公欠申請はまだありません。'
            : '許可しなかった公欠申請はありません。'}
        </p>
      ) : rows.map((r) => {
        const busy = savingId === r.id;
        return (
        <div key={r.id} className={Styles.row}>
          <p className={Styles.colSubmitted}>{formatShortDate(r.submittedAt)}</p>
          <div className={Styles.colName}>
            <p className={Styles.name}>{r.studentName || '(名前未登録)'}</p>
            {classOf.get(r.uid) && <p className={Styles.sub}>{classOf.get(r.uid)}</p>}
          </div>
          <div className={Styles.colStore}>
            {/* 企業ロゴ(保存タブの見学・説明会から出した申請は recruitId から分かる。無ければ既定の画像) */}
            <img
              className={Styles.logo}
              src={r.recruitId ? logoUrl(r.recruitId) : CompanyDefaultImage}
              alt=""
              onError={(e) => { e.currentTarget.src = CompanyDefaultImage; }}
            />
            <div className={Styles.storeText}>
              <p className={Styles.store}>{r.companyName || r.storeName}</p>
              {r.companyName && r.storeName && <p className={Styles.sub}>{r.storeName}</p>}
            </div>
          </div>
          <p className={Styles.colType}>{typeLabel(r)}</p>
          <div className={Styles.colSchedule}>
            <p>
              {formatShortDate(r.date)}
              {r.date && <span className={Styles.week}>({WEEK[r.date.getDay()]})</span>}
            </p>
            <p className={Styles.sub}>{r.startTime}〜{r.endTime}</p>
          </div>
          <p className={Styles.colPlace}>{r.place || '-'}</p>

          <div className={Styles.colIcon}>
            <button type="button" className={Styles.iconBtn} title="詳細を見る" aria-label="詳細を見る" onClick={() => setDetail(r)}>
              <HiOutlineDocumentText />
            </button>
          </div>
          {/* 許可待ちタブは○×どちらも押せる。許可済み・不許可タブは、どちらを選んだかの印は出さず
              「許可待ちに戻す」1つだけにする(押し間違いの取り消し用) */}
          {tab === 'submitted' ? (
            <>
              <div className={Styles.colIcon}>
                <button type="button" title="許可する" aria-label="許可する"
                  className={`${Styles.decideBtn} ${Styles.approveBtn}`}
                  disabled={busy}
                  // 公欠取得なし(hasAbsence !== true)の報告は、公欠取得時間を選ぶ意味がないので
                  // ポップアップを出さずそのまま許可する。
                  onClick={() => (r.hasAbsence ? setPeriodPicker(r) : decide(r, true))}>
                  <FaRegCircle />
                </button>
              </div>
              <div className={Styles.colIcon}>
                <button type="button" title="許可しない" aria-label="許可しない"
                  className={`${Styles.decideBtn} ${Styles.rejectBtn}`}
                  disabled={busy}
                  onClick={() => decide(r, false)}>
                  <RxCross2 className={Styles.crossIcon} />
                </button>
              </div>
            </>
          ) : tab === 'approved' ? (
            <>
              <div className={Styles.colPdf}>
                <button type="button" title="参加証明書を出力" aria-label="参加証明書を出力"
                  className={Styles.pdfIconBtn}
                  disabled={!!pdfLoadingKey}
                  onClick={() => exportPdf(r, 'certificate')}>
                  {pdfLoadingKey === `${r.id}:certificate` ? <span className={Styles.pdfSpinner} /> : '参'}
                </button>
                <button type="button" title="就職活動用紙を出力" aria-label="就職活動用紙を出力"
                  className={Styles.pdfIconBtn}
                  disabled={!!pdfLoadingKey}
                  onClick={() => exportPdf(r, 'activitySheet')}>
                  {pdfLoadingKey === `${r.id}:activitySheet` ? <span className={Styles.pdfSpinner} /> : '就'}
                </button>
              </div>
              <div className={Styles.colIconWide}>
                <button type="button" title="許可待ちに戻す" aria-label="許可待ちに戻す"
                  className={`${Styles.decideBtn} ${Styles.resetBtn}`}
                  disabled={busy}
                  onClick={() => resetToPending(r)}>
                  <IoRefreshOutline />
                </button>
              </div>
            </>
          ) : (
            <div className={Styles.colIconWide}>
              <button type="button" title="許可待ちに戻す" aria-label="許可待ちに戻す"
                className={`${Styles.decideBtn} ${Styles.resetBtn}`}
                disabled={busy}
                onClick={() => resetToPending(r)}>
                <IoRefreshOutline />
              </button>
            </div>
          )}
        </div>
        );
      })}
    </div>

    {detail && <DetailDialog request={detail} className={classOf.get(detail.uid) ?? ''} onClose={() => setDetail(null)} />}
    {periodPicker && (
      <PeriodPickerDialog
        request={periodPicker}
        onClose={() => setPeriodPicker(null)}
        onConfirm={(periods) => {
          decide(periodPicker, true, periods);
          setPeriodPicker(null);
        }}
      />
    )}
    </>
  );
};

// ○(許可する)を押した直後に出す、公欠取得時間(Ｈ１２３４５６Ｈ)を選ぶポップアップ。
// 複数タップでON/OFFを切り替えられる(連続・不連続どちらも選べる)。何も選ばなくても許可できる。
const PeriodPickerDialog: React.FC<{
  request: AbsenceRequest;
  onClose: () => void;
  onConfirm: (periods: string[]) => void;
}> = ({ request: r, onClose, onConfirm }) => {
  const [ periods, setPeriods ] = useState<string[]>([]);
  const toggle = (key: string) =>
    setPeriods((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [ ...prev, key ]));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className={Styles.modal} role="dialog" aria-modal="true" aria-labelledby="period-picker-title">
      <div className={Styles.modal_bg} onClick={onClose} />
      <div className={`${Styles.modal_contents} ${Styles.periodModal}`}>
        <div className={Styles.modal_title}>
          <div>
            <h4 id="period-picker-title">{r.studentName || '(名前未登録)'}を許可する</h4>
            <p className={Styles.modal_title_sub}>公欠取得時間を選んでください(就職活動用紙に反映されます)</p>
          </div>
          <RxCross2 className={Styles.modal_cross} onClick={onClose} aria-label="閉じる" />
        </div>

        <div className={Styles.periodGrid}>
          {PERIOD_ORDER.map((key) => (
            <button
              key={key}
              type="button"
              className={`${Styles.periodChip} ${periods.includes(key) ? Styles.periodChipActive : ''}`}
              onClick={() => toggle(key)}>
              {PERIOD_LABELS[key]}
            </button>
          ))}
        </div>

        <div className={Styles.periodActions}>
          <button type="button" className={Styles.periodCancel} onClick={onClose}>キャンセル</button>
          <button type="button" className={Styles.periodConfirm} onClick={() => onConfirm(periods)}>許可する</button>
        </div>
      </div>
    </div>
  );
};

const STATUS_LABELS: Record<Status, string> = {
  submitted: '許可待ち',
  approved: '許可済み',
  rejected: '不許可',
};

const formatLongDate = (d: Date | null) =>
  (d ? `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日(${WEEK[d.getDay()]})` : '-');

// 申請の詳細(ガイダンス回答状況の詳細と同じ見た目。灰色の地に白いまとまり)
const DetailDialog: React.FC<{ request: AbsenceRequest; className: string; onClose: () => void }> = ({ request: r, className, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const section = (label: string, items: [string, React.ReactNode][]) => (
    <div className={Styles.modal_section}>
      <p className={Styles.modal_section_head}>{label}</p>
      {items.map(([ k, v ]) => (
        <div key={k} className={Styles.modal_item}>
          <p className={Styles.modal_item_label}>{k}</p>
          <div className={Styles.modal_item_value}>{v}</div>
        </div>
      ))}
    </div>
  );

  const progress: [string, React.ReactNode][] = [
    [ '状態', <span className={Styles[`status_${r.status}`]}>{STATUS_LABELS[r.status]}</span> ],
    [ '申請日', formatLongDate(r.submittedAt) ],
  ];
  if (r.status !== 'submitted') progress.push([ r.status === 'approved' ? '許可日' : '不許可日', formatLongDate(r.decidedAt) ]);
  if (r.status === 'approved' && r.approvedPeriods.length > 0) {
    progress.push([ '公欠取得時間', PERIOD_ORDER.filter((p) => r.approvedPeriods.includes(p)).map((p) => PERIOD_LABELS[p]).join(' ') ]);
  }

  return (
    <div className={Styles.modal} role="dialog" aria-modal="true" aria-labelledby="absence-detail-title">
      <div className={Styles.modal_bg} onClick={onClose} />
      <div className={Styles.modal_contents}>
        <div className={Styles.modal_title}>
          <div>
            <h4 id="absence-detail-title">{r.studentName || '(名前未登録)'}</h4>
            {className && <p className={Styles.modal_title_sub}>{className}</p>}
          </div>
          <RxCross2 className={Styles.modal_cross} onClick={onClose} aria-label="閉じる" />
        </div>

        {section('申請内容', [
          [ '申請種別', typeLabel(r) ],
          [ '実施日', formatLongDate(r.date) ],
          [ '時間', `${r.startTime}〜${r.endTime}` ],
          [ '場所', r.place || '-' ],
        ])}

        {section('サロン', [
          [ 'サロン名', (
            <span className={Styles.modal_salon}>
              <img
                className={Styles.logo}
                src={r.recruitId ? logoUrl(r.recruitId) : CompanyDefaultImage}
                alt=""
                onError={(e) => { e.currentTarget.src = CompanyDefaultImage; }}
              />
              {r.companyName || '-'}
            </span>
          ) ],
          [ '店舗名', r.storeName || '-' ],
        ])}

        {section('許可の状況', progress)}
      </div>
    </div>
  );
};

export default AbsenceRequests;
